import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/client';
import Pagination from '../components/Pagination';
import { useNotification } from '../components/Notification';
import ConfirmModal from '../components/ConfirmModal';
import { ViewIcon, EditIcon, DeleteIcon, AddIcon } from '../components/Icons';
import './AdminCoupons.css';

const LOCALES = [
  { code: 'lt', label: 'Lietuvių' },
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
];

function AdminCoupons() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const { success, error: showError } = useNotification();
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, onConfirm: null, message: '', title: '', type: 'warning' });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all', // all, active, inactive
  });
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [editForm, setEditForm] = useState({
    code: '',
    name_lt: '',
    name_en: '',
    name_de: '',
    description_lt: '',
    description_en: '',
    description_de: '',
    discount_type: 'percentage',
    discount_value: 0,
    min_order_value: null,
    max_discount: null,
    usage_limit: null,
    usage_limit_per_user: 1,
    is_active: true,
    valid_from: '',
    valid_until: '',
    applicable_to: 'all',
    applicable_ids: [],
    buy_x_get_y_config: null,
    gift_product_id: null
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUsageModal, setShowUsageModal] = useState(false);
  const [selectedCouponId, setSelectedCouponId] = useState(null);

  const { data: couponsData, isLoading } = useQuery({
    queryKey: ['admin-coupons', page, limit, filters],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit === 'all' ? '999999' : limit.toString(),
      });
      
      if (filters.search) params.append('search', filters.search);
      if (filters.status !== 'all') params.append('is_active', filters.status === 'active' ? '1' : '0');

      const res = await apiClient.get(`/admin/coupons?${params.toString()}`);
      return res.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data) => {
      return apiClient.post('/admin/coupons', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-coupons']);
      setShowCreateModal(false);
      setEditingCoupon(null);
      success('Kuponas sėkmingai sukurtas');
    },
    onError: (error) => {
      showError(error.response?.data?.error || 'Klaida kuriant kupono');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      return apiClient.put(`/admin/coupons/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-coupons']);
      setShowCreateModal(false);
      setEditingCoupon(null);
      success('Kuponas sėkmingai atnaujintas');
    },
    onError: (error) => {
      showError(error.response?.data?.error || 'Klaida atnaujinant kupono');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      return apiClient.delete(`/admin/coupons/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-coupons']);
      success('Kuponas pašalintas');
    },
    onError: (error) => {
      showError(error.response?.data?.error || 'Klaida šalinant kupono');
    },
  });

  const handleEdit = (coupon) => {
    setEditingCoupon(coupon);
    setEditForm({
      code: coupon.code || '',
      name_lt: coupon.name_lt ?? coupon.name ?? '',
      name_en: coupon.name_en ?? '',
      name_de: coupon.name_de ?? '',
      description_lt: coupon.description_lt ?? coupon.description ?? '',
      description_en: coupon.description_en ?? '',
      description_de: coupon.description_de ?? '',
      discount_type: coupon.discount_type || 'percentage',
      discount_value: coupon.discount_value ? parseFloat(coupon.discount_value) : 0,
      min_order_value: coupon.min_order_value ? parseFloat(coupon.min_order_value) : null,
      max_discount: coupon.max_discount ? parseFloat(coupon.max_discount) : null,
      usage_limit: coupon.usage_limit ? parseInt(coupon.usage_limit) : null,
      usage_limit_per_user: coupon.usage_limit_per_user || 1,
      is_active: coupon.is_active !== undefined ? coupon.is_active : true,
      valid_from: coupon.valid_from ? coupon.valid_from.split('T')[0] + 'T' + coupon.valid_from.split('T')[1]?.substring(0, 5) : '',
      valid_until: coupon.valid_until ? coupon.valid_until.split('T')[0] + 'T' + coupon.valid_until.split('T')[1]?.substring(0, 5) : '',
      applicable_to: coupon.applicable_to || 'all',
      applicable_ids: coupon.applicable_ids || [],
      buy_x_get_y_config: coupon.buy_x_get_y_config || null,
      gift_product_id: coupon.gift_product_id || null
    });
    setShowCreateModal(true);
  };

  const handleNew = () => {
    setEditingCoupon(null);
    setEditForm({
      code: '',
      name_lt: '',
      name_en: '',
      name_de: '',
      description_lt: '',
      description_en: '',
      description_de: '',
      discount_type: 'percentage',
      discount_value: 0,
      min_order_value: null,
      max_discount: null,
      usage_limit: null,
      usage_limit_per_user: 1,
      is_active: true,
      valid_from: '',
      valid_until: '',
      applicable_to: 'all',
      applicable_ids: [],
      buy_x_get_y_config: null,
      gift_product_id: null
    });
    setShowCreateModal(true);
  };

  const handleSave = () => {
    const hasName = (editForm.name_lt || '').trim() || (editForm.name_en || '').trim() || (editForm.name_de || '').trim();
    if (!editForm.code || !hasName || !editForm.discount_type) {
      notification.info('Užpildykite kodą, pavadinimą bent viena kalba ir nuolaidos tipą');
      return;
    }

    const dataToSend = {
      ...editForm,
      code: editForm.code.toUpperCase().trim(),
      valid_from: editForm.valid_from || null,
      valid_until: editForm.valid_until || null,
      applicable_ids: editForm.applicable_ids.length > 0 ? editForm.applicable_ids : null
    };

    if (editingCoupon) {
      updateMutation.mutate({ id: editingCoupon.id, data: dataToSend });
    } else {
      createMutation.mutate(dataToSend);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('lt-LT');
  };

  if (isLoading) {
    return <div className="admin-coupons-page container">Kraunama...</div>;
  }

  return (
    <div className="admin-coupons-page container">
      <div className="coupons-header">
        <h1>Kuponai</h1>
        <button onClick={handleNew} className="btn btn-primary btn-sm">
          <AddIcon />
          Pridėti kupono
        </button>
      </div>

      {/* Filtrai */}
      <div className="filters-section">
        <div className="filters-grid">
          <div className="filter-group">
            <label>Paieška:</label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => {
                setFilters({ ...filters, search: e.target.value });
                setPage(1);
              }}
              className="input"
              placeholder="Ieškoti pagal kodą, pavadinimą..."
            />
          </div>
          <div className="filter-group">
            <label>Statusas:</label>
            <select
              value={filters.status}
              onChange={(e) => {
                setFilters({ ...filters, status: e.target.value });
                setPage(1);
              }}
              className="input"
            >
              <option value="all">Visi</option>
              <option value="active">Aktyvūs</option>
              <option value="inactive">Neaktyvūs</option>
            </select>
          </div>
          <div className="filter-group">
            <button
              onClick={() => {
                setFilters({ search: '', status: 'all' });
                setPage(1);
              }}
              className="btn btn-secondary"
            >
              Išvalyti
            </button>
          </div>
        </div>
      </div>

      <div className="coupons-table-wrapper">
        <table className="coupons-table">
          <thead>
            <tr>
              <th>Kodas</th>
              <th>Pavadinimas</th>
              <th>Tipas</th>
              <th>Nuolaida</th>
              <th>Naudojimai</th>
              <th>Galioja iki</th>
              <th>Statusas</th>
              <th>Veiksmai</th>
            </tr>
          </thead>
          <tbody>
            {couponsData?.coupons && couponsData.coupons.length > 0 ? (
              couponsData.coupons.map((coupon) => (
              <tr key={coupon.id}>
                <td>
                  <strong>{coupon.code}</strong>
                </td>
                <td>{coupon.name}</td>
                <td>
                  {coupon.discount_type === 'percentage' && 'Procentinė'}
                  {coupon.discount_type === 'fixed' && 'Fiksuota'}
                  {coupon.discount_type === 'free_shipping' && 'Nemokamas pristatymas'}
                  {coupon.discount_type === 'buy_x_get_y' && '2 už 1'}
                  {coupon.discount_type === 'gift' && 'Dovana'}
                </td>
                <td>
                  {coupon.discount_type === 'percentage' && `${coupon.discount_value}%`}
                  {coupon.discount_type === 'fixed' && `${parseFloat(coupon.discount_value).toFixed(2)} €`}
                  {coupon.discount_type === 'free_shipping' && 'Nemokamas pristatymas'}
                  {coupon.discount_type === 'buy_x_get_y' && '2 už 1'}
                  {coupon.discount_type === 'gift' && 'Dovana'}
                </td>
                <td>
                  {coupon.used_count || 0} / {coupon.usage_limit || '∞'}
                </td>
                <td>{formatDate(coupon.valid_until)}</td>
                <td>
                  <span className={`status-badge ${coupon.is_active ? 'active' : 'inactive'}`}>
                    {coupon.is_active ? 'Aktyvus' : 'Neaktyvus'}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      onClick={() => {
                        setSelectedCouponId(coupon.id);
                        setShowUsageModal(true);
                      }}
                      className="btn-icon btn-icon-secondary"
                      title="Istorija"
                    >
                      <ViewIcon />
                    </button>
                    <button
                      onClick={() => handleEdit(coupon)}
                      className="btn-icon btn-icon-secondary"
                      title="Redaguoti"
                    >
                      <EditIcon />
                    </button>
                    <button
                      onClick={() => {
                        setConfirmModal({
                          isOpen: true,
                          title: 'Pašalinti kuponą',
                          message: `Ar tikrai norite pašalinti kupono "${coupon.name}"?`,
                          type: 'danger',
                          confirmText: 'Pašalinti',
                          onConfirm: () => {
                            deleteMutation.mutate(coupon.id);
                            setConfirmModal({ isOpen: false, onConfirm: null, message: '', title: '', type: 'warning' });
                          },
                          onCancel: () => setConfirmModal({ isOpen: false, onConfirm: null, message: '', title: '', type: 'warning' })
                        });
                      }}
                      className="btn-icon btn-icon-danger"
                      title="Pašalinti"
                    >
                      <DeleteIcon />
                    </button>
                  </div>
                </td>
              </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" style={{ textAlign: 'center', padding: '40px' }}>
                  Kuponų nėra
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Puslapiavimas */}
      {couponsData?.pagination && (
        <Pagination
          page={couponsData.pagination.page}
          totalPages={couponsData.pagination.pages}
          totalItems={couponsData.pagination.total}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={setLimit}
        />
      )}

      {/* Redagavimo/Sukūrimo modalas */}
      {showCreateModal && (
        <CouponModal
          editForm={editForm}
          setEditForm={setEditForm}
          editingCoupon={editingCoupon}
          onSave={handleSave}
          onClose={() => {
            setShowCreateModal(false);
            setEditingCoupon(null);
          }}
          isLoading={createMutation.isLoading || updateMutation.isLoading}
        />
      )}

      {/* Naudojimų istorijos modalas */}
      {showUsageModal && (
        <CouponUsageModal
          couponId={selectedCouponId}
          onClose={() => {
            setShowUsageModal(false);
            setSelectedCouponId(null);
          }}
        />
      )}

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        confirmText={confirmModal.confirmText}
        onConfirm={confirmModal.onConfirm}
        onCancel={confirmModal.onCancel}
      />
    </div>
  );
}

// Kupono modalas komponentas
function CouponModal({ editForm, setEditForm, editingCoupon, onSave, onClose, isLoading }) {
  const [activeLang, setActiveLang] = useState('lt');
  const hasName = (editForm.name_lt || '').trim() || (editForm.name_en || '').trim() || (editForm.name_de || '').trim();
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
        <h2>{editingCoupon ? 'Redaguoti kupono' : 'Naujas kuponas'}</h2>

        <div className="form-group">
          <label>Kodas *</label>
          <input
            type="text"
            value={editForm.code}
            onChange={(e) => setEditForm({ ...editForm, code: e.target.value.toUpperCase() })}
            className="input"
            placeholder="SUMMER2024"
            required
            disabled={!!editingCoupon}
          />
        </div>

        <p className="admin-lang-hint">Pavadinimas ir aprašymas – įveskite bent viena kalba (LT / EN / DE).</p>
        <div className="admin-lang-tabs">
          {LOCALES.map(({ code, label }) => (
            <button
              key={code}
              type="button"
              className={`admin-lang-tab ${activeLang === code ? 'active' : ''}`}
              onClick={() => setActiveLang(code)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="form-group">
          <label>Pavadinimas ({LOCALES.find(l => l.code === activeLang)?.label}) *</label>
          <input
            type="text"
            value={editForm[`name_${activeLang}`] ?? ''}
            onChange={(e) => setEditForm({ ...editForm, [`name_${activeLang}`]: e.target.value })}
            className="input"
            placeholder={activeLang === 'lt' ? 'Pavadinimas' : activeLang === 'en' ? 'Name' : 'Bezeichnung'}
          />
        </div>

        <div className="form-group">
          <label>Aprašymas ({LOCALES.find(l => l.code === activeLang)?.label})</label>
          <textarea
            value={editForm[`description_${activeLang}`] ?? ''}
            onChange={(e) => setEditForm({ ...editForm, [`description_${activeLang}`]: e.target.value })}
            className="input"
            rows="3"
            placeholder={activeLang === 'lt' ? 'Aprašymas' : activeLang === 'en' ? 'Description' : 'Beschreibung'}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Nuolaidos tipas *</label>
            <select
              value={editForm.discount_type}
              onChange={(e) => setEditForm({ ...editForm, discount_type: e.target.value })}
              className="input"
            >
              <option value="percentage">Procentinė nuolaida</option>
              <option value="fixed">Fiksuota suma</option>
              <option value="free_shipping">Nemokamas pristatymas</option>
              <option value="buy_x_get_y">2 už 1 (Buy X Get Y)</option>
              <option value="gift">Dovana</option>
            </select>
          </div>

          {editForm.discount_type !== 'free_shipping' && editForm.discount_type !== 'gift' && (
            <div className="form-group">
              <label>
                {editForm.discount_type === 'percentage' ? 'Nuolaida (%)' : 'Nuolaida (€)'} *
              </label>
              <input
                type="number"
                step={editForm.discount_type === 'percentage' ? '1' : '0.01'}
                value={editForm.discount_value}
                onChange={(e) => setEditForm({ ...editForm, discount_value: parseFloat(e.target.value) || 0 })}
                className="input"
                required
              />
            </div>
          )}
        </div>

        {editForm.discount_type === 'percentage' && (
          <div className="form-group">
            <label>Maksimali nuolaida (€)</label>
            <input
              type="number"
              step="0.01"
              value={editForm.max_discount || ''}
              onChange={(e) => setEditForm({ ...editForm, max_discount: e.target.value ? parseFloat(e.target.value) : null })}
              className="input"
            />
          </div>
        )}

        <div className="form-row">
          <div className="form-group">
            <label>Min. užsakymo suma (€)</label>
            <input
              type="number"
              step="0.01"
              value={editForm.min_order_value || ''}
              onChange={(e) => setEditForm({ ...editForm, min_order_value: e.target.value ? parseFloat(e.target.value) : null })}
              className="input"
            />
          </div>

          <div className="form-group">
            <label>Naudojimų limitas</label>
            <input
              type="number"
              value={editForm.usage_limit || ''}
              onChange={(e) => setEditForm({ ...editForm, usage_limit: e.target.value ? parseInt(e.target.value) : null })}
              className="input"
              placeholder="∞"
            />
          </div>
        </div>

        <div className="form-group">
          <label>Naudojimų limitas vienam vartotojui</label>
          <input
            type="number"
            value={editForm.usage_limit_per_user}
            onChange={(e) => setEditForm({ ...editForm, usage_limit_per_user: parseInt(e.target.value) || 1 })}
            className="input"
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Galioja nuo</label>
            <input
              type="datetime-local"
              value={editForm.valid_from}
              onChange={(e) => setEditForm({ ...editForm, valid_from: e.target.value })}
              className="input"
            />
          </div>

          <div className="form-group">
            <label>Galioja iki</label>
            <input
              type="datetime-local"
              value={editForm.valid_until}
              onChange={(e) => setEditForm({ ...editForm, valid_until: e.target.value })}
              className="input"
            />
          </div>
        </div>

        <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={editForm.is_active}
              onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
            />
            <span>Aktyvus</span>
          </label>
        </div>

        <div className="modal-actions">
          <button onClick={onClose} className="btn btn-secondary">
            Atšaukti
          </button>
          <button
            onClick={onSave}
            disabled={isLoading || !editForm.code || !hasName}
            className="btn btn-primary"
          >
            {isLoading ? 'Išsaugoma...' : 'Išsaugoti'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Naudojimų istorijos modalas
function CouponUsageModal({ couponId, onClose }) {
  const { data: usageData, isLoading } = useQuery({
    queryKey: ['coupon-usage', couponId],
    queryFn: async () => {
      const res = await apiClient.get(`/admin/coupons/${couponId}/usage`);
      return res.data;
    },
    enabled: !!couponId,
  });

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleString('lt-LT');
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>Kupono naudojimų istorija</h2>

        {isLoading ? (
          <div>Kraunama...</div>
        ) : usageData?.usage && usageData.usage.length > 0 ? (
          <div className="usage-table-wrapper">
            <table className="usage-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Užsakymas</th>
                  <th>Vartotojas</th>
                  <th>Nuolaida</th>
                </tr>
              </thead>
              <tbody>
                {usageData.usage.map((usage) => (
                  <tr key={usage.id}>
                    <td>{formatDate(usage.used_at)}</td>
                    <td>{usage.order_number || `#${usage.order_id}`}</td>
                    <td>
                      {usage.user_email || (usage.first_name && usage.last_name
                        ? `${usage.first_name} ${usage.last_name}`
                        : 'Svečias')}
                    </td>
                    <td>{parseFloat(usage.discount_amount).toFixed(2)} €</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p>Naudojimų nėra</p>
        )}

        <div className="modal-actions">
          <button onClick={onClose} className="btn btn-secondary">
            Uždaryti
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdminCoupons;
